import Header from '@risk-smart/themed-cloudscape-components/header';
import Button from '@risksmart-app/components/src/button';
import useRisksmartUser from '@risksmart-app/components/src/hooks/useRisksmartUser';
import Modal from '@risksmart-app/components/src/modal';
import { Attestation_Record_Status_Enum } from '@risksmart-app/web-graphql-client/generated/graphql';
import { Check } from '@untitled-ui/icons-react';
import type { FC } from 'react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { z } from 'zod';

import { useAttestRecord } from '@/hooks/mutations';
import { useGetAttestationStatus } from '@/hooks/queries';
import { handleError } from '@/utils/errorUtils';

import styles from './styles.module.scss';

const attestationStatusSchema = z.nativeEnum(Attestation_Record_Status_Enum);

type Props = {
  parentId: string;
};

export const AttestButton: FC<Props> = ({ parentId }) => {
  const { user, isLoading } = useRisksmartUser();
  const [modalVisible, setModalVisible] = useState(false);
  const { data, loading, refetch } = useGetAttestationStatus({
    queryArgs: { parentId, userId: user!.userId },
    shouldSkip: isLoading || !user,
  });
  const { t } = useTranslation(['common'], {
    keyPrefix: 'attestations',
  });

  const { attest, loading: mutating } = useAttestRecord();

  const attestationRecord = data?.attestation_record[0];

  const handleAttest = async () => {
    if (!attestationRecord) {
      return;
    }

    try {
      await attest({ Id: attestationRecord.Id });
      await refetch();
      setModalVisible(false);
    } catch (error) {
      // Mirrors the legacy Apollo path, where a failed attest was reported
      // via the global Apollo error link. tRPC errors flow through
      // tanstack-query (not the Apollo link chain), so report here and keep
      // the modal open so the user can retry.
      handleError(error);
    }
  };

  const statusString = attestationRecord?.attestationRecordStatus?.Status;
  const parsedStatus = statusString
    ? attestationStatusSchema.safeParse(statusString)
    : undefined;
  const status = parsedStatus?.success
    ? parsedStatus.data
    : attestationRecord?.AttestationStatus;

  const attested = status === Attestation_Record_Status_Enum.Attested;

  const notRequired =
    status === Attestation_Record_Status_Enum.NotRequired ||
    status === Attestation_Record_Status_Enum.Expired ||
    attestationRecord === undefined;

  return (
    <>
      <Button
        disabled={notRequired || attested}
        loading={loading || mutating}
        onClick={() => setModalVisible(true)}
        iconSvg={attested ? <Check viewBox={'0 0 24 24'} /> : null}
        {...{ className: attested ? styles.successButton : '' }}
      >
        {attested ? t('buttonText.attested') : t('buttonText.pending')}
      </Button>
      {modalVisible && (
        <Modal
          visible={true}
          header={<Header>{t('prompt.title')}</Header>}
          onDismiss={() => setModalVisible(false)}
        >
          <p className={'mt-0'}>
            {attestationRecord?.config?.PromptText ?? t('prompt.default')}
          </p>
          <div className={'flex flex-row gap-3'}>
            <Button
              loading={loading || mutating}
              variant={'primary'}
              onClick={handleAttest}
            >
              {t('prompt.confirm')}
            </Button>
            <Button onClick={() => setModalVisible(false)}>
              {t('prompt.cancel')}
            </Button>
          </div>
        </Modal>
      )}
    </>
  );
};
